import { forwardRef } from "react";

import {
  Table as DSTable,
  TableCell as DSTableCell,
  TableHeaderCell as DSTableHeaderCell,
  TableRow as DSTableRow,
} from "@hireart/component-library";
import clsx from "clsx";
import PropTypes from "prop-types";

/**
 * SUI Table props that should not be forwarded to the design system component.
 */
const SUI_TABLE_PROPS = [
  "sortable",
  "striped",
  "unstackable",
  "compact",
  "color",
  "selectable",
  "celled",
  "collapsing",
  "definition",
  "fixed",
  "padded",
  "singleLine",
  "stackable",
  "structured",
  "textAlign",
  "verticalAlign",
  "basic",
  "attached",
  "inverted",
  "size",
];

const SUI_ROW_PROPS = [
  "active",
  "positive",
  "negative",
  "warning",
  "error",
  "disabled",
  "textAlign",
  "verticalAlign",
];

const SUI_CELL_PROPS = [
  "collapsing",
  "textAlign",
  "verticalAlign",
  "width",
  "singleLine",
  "positive",
  "negative",
  "warning",
  "error",
  "active",
  "disabled",
  "selectable",
  "sorted",
];

const stripProps = (props, keys) => {
  const cleaned = { ...props };
  keys.forEach((key) => delete cleaned[key]);
  return cleaned;
};

const TableHeader = forwardRef(function TableHeader(
  { children, className, ...rest },
  ref
) {
  const cleanProps = stripProps(rest, ["fullWidth"]);
  return (
    <thead ref={ref} className={className} {...cleanProps}>
      {children}
    </thead>
  );
});

TableHeader.displayName = "TableHeader";
TableHeader.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  fullWidth: PropTypes.bool,
};

const TableBody = forwardRef(function TableBody(
  { children, className, ...rest },
  ref
) {
  return (
    <tbody ref={ref} className={className} {...rest}>
      {children}
    </tbody>
  );
});

TableBody.displayName = "TableBody";
TableBody.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

const TableFooter = forwardRef(function TableFooter(
  { children, className, ...rest },
  ref
) {
  const cleanProps = stripProps(rest, ["fullWidth"]);
  return (
    <tfoot ref={ref} className={className} {...cleanProps}>
      {children}
    </tfoot>
  );
});

TableFooter.displayName = "TableFooter";
TableFooter.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  fullWidth: PropTypes.bool,
};

const TableRow = forwardRef(function TableRow(
  { children, className, selectable, ...rest },
  ref
) {
  const cleanProps = stripProps(rest, SUI_ROW_PROPS);
  return (
    <DSTableRow
      ref={ref}
      className={className}
      hasHover={selectable || false}
      {...cleanProps}
    >
      {children}
    </DSTableRow>
  );
});

TableRow.displayName = "TableRow";
/* eslint-disable react/boolean-prop-naming -- SUI compatibility props */
TableRow.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  active: PropTypes.bool,
  positive: PropTypes.bool,
  negative: PropTypes.bool,
  warning: PropTypes.bool,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  selectable: PropTypes.bool,
  textAlign: PropTypes.string,
  verticalAlign: PropTypes.string,
};
/* eslint-enable react/boolean-prop-naming */

const TableCell = forwardRef(function TableCell(
  { children, className, ...rest },
  ref
) {
  const cleanProps = stripProps(rest, SUI_CELL_PROPS);
  return (
    <DSTableCell ref={ref} className={className} {...cleanProps}>
      {children}
    </DSTableCell>
  );
});

TableCell.displayName = "TableCell";
/* eslint-disable react/boolean-prop-naming -- SUI compatibility props */
TableCell.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  collapsing: PropTypes.bool,
  textAlign: PropTypes.string,
  verticalAlign: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  singleLine: PropTypes.bool,
  positive: PropTypes.bool,
  negative: PropTypes.bool,
  warning: PropTypes.bool,
  error: PropTypes.bool,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  selectable: PropTypes.bool,
};
/* eslint-enable react/boolean-prop-naming */

const TableHeaderCell = forwardRef(function TableHeaderCell(
  { children, className, ...rest },
  ref
) {
  const cleanProps = stripProps(rest, SUI_CELL_PROPS);
  return (
    <DSTableHeaderCell ref={ref} className={className} {...cleanProps}>
      {children}
    </DSTableHeaderCell>
  );
});

TableHeaderCell.displayName = "TableHeaderCell";
/* eslint-disable react/boolean-prop-naming -- SUI compatibility props */
TableHeaderCell.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  collapsing: PropTypes.bool,
  textAlign: PropTypes.string,
  verticalAlign: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  singleLine: PropTypes.bool,
  sorted: PropTypes.string,
};
/* eslint-enable react/boolean-prop-naming */

export const Table = forwardRef(function Table(
  { children, className, selectable, ...rest },
  ref
) {
  const cleanProps = stripProps(rest, SUI_TABLE_PROPS);
  return (
    <DSTable ref={ref} className={clsx(className)} {...cleanProps}>
      {children}
    </DSTable>
  );
});

Table.displayName = "Table";

/* eslint-disable react/boolean-prop-naming -- SUI compatibility props */
Table.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  sortable: PropTypes.bool,
  striped: PropTypes.bool,
  unstackable: PropTypes.bool,
  compact: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  color: PropTypes.string,
  selectable: PropTypes.bool,
  celled: PropTypes.bool,
  collapsing: PropTypes.bool,
  definition: PropTypes.bool,
  fixed: PropTypes.bool,
  padded: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  singleLine: PropTypes.bool,
  stackable: PropTypes.bool,
  structured: PropTypes.bool,
  textAlign: PropTypes.string,
  verticalAlign: PropTypes.string,
  basic: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  attached: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  inverted: PropTypes.bool,
  size: PropTypes.string,
};
/* eslint-enable react/boolean-prop-naming */

Table.Header = TableHeader;
Table.Body = TableBody;
Table.Footer = TableFooter;
Table.Row = TableRow;
Table.Cell = TableCell;
Table.HeaderCell = TableHeaderCell;
